import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { DiscountReportPage } from '@/features/reports/pages/discount-report'
import { requireCapability } from '@/lib/auth'

import type { DiscountReportState, ReportRangeSelection } from '@/types'

const PAGE_SIZE = 50

/* Reports → Discounts. The range, the tab and every filter live in the URL so
   "the stacked discounts last month" can be shared. */
const searchSchema = reportRangeSearchSchema.extend({
  tab: z.enum(['summary', 'promotions', 'leaks', 'customers', 'details']).optional(),
  kind: z.string().optional(),
  rule: z.string().optional(),
  bucket: z.enum(['promotion', 'loyalty', 'manual', 'employee']).optional(),
  only: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
})

export const Route = createFileRoute('/_dashboard/reports/discounts')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: DiscountReportRoute,
})

function DiscountReportRoute() {
  const { tab, kind, rule, bucket, only, page, limit, ...range } = Route.useSearch()
  const navigate = Route.useNavigate()
  // Promotions are judged over weeks: default to the last 30 days.
  const selection: ReportRangeSelection = range.mode === 'preset' && !range.preset ? { ...range, preset: '30d' } : range
  const state: DiscountReportState = { tab: tab ?? 'summary', kind, rule, bucket, only, page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <DiscountReportPage
      selection={selection}
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      state={state}
      onStateChange={(next) =>
        navigate({
          search: (prev) => ({
            ...prev,
            tab: next.tab === 'summary' ? undefined : next.tab,
            kind: next.kind, rule: next.rule, bucket: next.bucket, only: next.only || undefined,
            page: next.page > 1 ? next.page : undefined,
            limit: next.limit !== PAGE_SIZE ? next.limit : undefined,
          }),
          replace: true,
        })
      }
    />
  )
}
